<script>
import { computed, defineComponent, ref } from "vue";
import { useGenericDocument } from "@/composables/useGenericDocument";
import { useDocuments } from "@/composables/useDocuments";
import { useFormField } from "@/composables/useFormField";
import { isUrlSafeString } from "@/lib/typeHelpers/urlFunctions/isUrlSafe";
import { useUserPermissions } from "@/composables/useUserPermissions";
import { isInteger } from "@/lib/getVariableType";

export default defineComponent({
  name: "InternshipFields",
  components: {},
  props: {
    repoId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const isSent = ref(false);

    const { user, hasAppRole } = useUserPermissions();
    const canEdit = computed(() => {
      return (
        hasAppRole("digiqt", "teacher") &&
        document.value._permissions === 5 &&
        document.value.host === user.value.repoId
      );
    });
    const isEditing = ref(false);

    const { documents, loadDocuments } = useDocuments("University", {
      refresh: true,
    });

    loadDocuments();

    const selectedUniversityTitle = ref(null);
    const selectedUniversity = ref(null);
    const noUniError = computed(() => {
      if (!selectedUniversityTitle.value !== null && !!selectedUniversity.value)
        return "Please select a university";
      return "";
    });
    const universityTitles = computed(() =>
      documents.value.map((item) => item.name)
    );
    const onSelectUniversity = (selected) => {
      selectedUniversityTitle.value = selected;
      selectedUniversity.value =
        documents.value?.find((item) => item.name === selected) ?? null;
    };

    const { document, updateDocument } = useGenericDocument(
      "Internship",
      props.repoId
    );

    const nameField = useFormField("name", {
      isRequired: true,
      value: document.value.name,
      tests: [
        {
          title: "Should use url-compatible characters only",
          validator: (v) =>
            isUrlSafeString(v, true)
              ? ""
              : "Name should use url-compatible characters only",
        },
      ],
    });
    const contactField = useFormField("contact", {
      isRequired: true,
      value: document.value.contact,
    });

    const mandatoryAttendanceField = useFormField("mandatoryAttendance", {
      isRequired: true,
      value: document.value.mandatoryAttendance,
    });
    const participationField = useFormField("participation", {
      isRequired: true,
      value: document.value.participation,
    });
    const prerequisitesField = useFormField("prerequisites", {
      isRequired: true,
      value: document.value.prerequisites,
    });
    const hoursPerWeekField = useFormField("hoursPerWeek", {
      isRequired: true,
      value: document.value.hoursPerWeek,
      tests: [
        {
          title: "Should be an integer",
          validator: (v) => {
            if (!isInteger(v, true)) return "Should be a whole number";
          },
        },
      ],
    });

    const formIsValid = computed(
      () =>
        nameField.isValid &&
        // descriptionField.isValid &&
        // minSeatsField.isValid &&
        // maxSeatsField.isValid &&
        participationField.isValid &&
        // startDateField.isValid &&
        // endDateField.isValid &&
        // applicationStartDateField.isValid &&
        // applicationEndDateField.isValid &&
        prerequisitesField.isValid &&
        // competenceFrameworkField.isValid &&
        // formOfTeachingField.isValid &&
        mandatoryAttendanceField.isValid &&
        hoursPerWeekField.isValid &&
        // digiQModulesField.isValid &&
        !!selectedUniversity.value
    );
    const assertFieldsAreValid = () => {
      if (formIsValid.value) return;
      throw new Error("Invalid field(s)");
    };
    const onUpdate = () => {
      assertFieldsAreValid();
      updateDocument({
        name: nameField.field.value,
        contactField: contactField.field.value,
        // description: descriptionField.field.value,
        // minSeats: minSeatsField.field.value,
        // maxSeats: maxSeatsField.field.value,
        participation: participationField.field.value,
        // startDate: startDateField.field.value,
        // endDate: endDateField.field.value,
        // applicationStartDate: applicationStartDateField.field.value,
        // applicationEndDate: applicationEndDateField.field.value,
        prerequisites: prerequisitesField.field.value,
        // competenceFramework: competenceFrameworkField.field.value,
        // formOfTeaching: formOfTeachingField.field.value,
        mandatoryAttendance: mandatoryAttendanceField.field.value,
        hoursPerWeek: hoursPerWeekField.field.value,
        // digiQModules: digiQModulesField.field.value,
        universityRepoId: selectedUniversity.value.repoId,
      });
    };

    return {
      canEdit,
      isEditing,
      document,
      contactField,
      formIsValid,
      hoursPerWeekField,
      isSent,
      mandatoryAttendanceField,
      prerequisitesField,
      nameField,
      noUniError,
      onSelectUniversity,
      onUpdate,
      participationField,
      selectedUniversityTitle,
      universityTitles,
      updateDocument,
    };
  },
});
</script>

<script setup>
import { PhBellRinging, PhBook, PhCalendarBlank } from "@phosphor-icons/vue";
</script>

<template>
  <div class="lInternshipFields">
    <div class="z-10">
      <slot name="isPrivate"></slot>
    </div>

    <div class="text-xl font-bold">
      <slot name="name"></slot>
    </div>

    <div class="w-full flex mb-2 items-center text-gray-600">
      <div class="text-sm w-fit pr-2">
        <slot name="area"></slot>
      </div>
      <div class="w-full text-sm border-l border-l-gray-600 pl-2">
        <slot name="university"></slot>
      </div>
    </div>

    <div class="space-y-0.5">
      <div class="w-full flex gap-1 items-center">
        <PhCalendarBlank :size="24" />
        <span class="text-gray-600 text-xs">Duration:</span>
        <span class="text-xs line-clamp-1">
          <slot name="duration"></slot>
        </span>
      </div>
      <div class="w-full flex gap-1 items-center">
        <ph-book :size="24" class="shrink-0" />
        <span class="text-gray-600 text-xs">Prerequisites:</span>
        <span class="text-xs line-clamp-1">
          <slot name="prerequisites"></slot>
        </span>
      </div>
    </div>
    <!--        <div class="w-full flex mb-2 gap-1">
              <div class="w-full flex gap-3 items-center">
                <svg class="w-4 h-4 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                  <path fill="currentColor" d="M6 1a1 1 0 0 0-2 0h2ZM4 4a1 1 0 0 0 2 0H4Zm7-3a1 1 0 1 0-2 0h2ZM9 4a1 1 0 1 0 2 0H9Zm7-3a1 1 0 1 0-2 0h2Zm-2 3a1 1 0 1 0 2 0h-2ZM1 6a1 1 0 0 0 0 2V6Zm18 2a1 1 0 1 0 0-2v2ZM5 11v-1H4v1h1Zm0 .01H4v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM10 11v-1H9v1h1Zm0 .01H9v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM10 15v-1H9v1h1Zm0 .01H9v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM15 15v-1h-1v1h1Zm0 .01h-1v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM15 11v-1h-1v1h1Zm0 .01h-1v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM5 15v-1H4v1h1Zm0 .01H4v1h1v-1Zm.01 0v1h1v-1h-1Zm0-.01h1v-1h-1v1ZM2 4h16V2H2v2Zm16 0h2a2 2 0 0 0-2-2v2Zm0 0v14h2V4h-2Zm0 14v2a2 2 0 0 0 2-2h-2Zm0 0H2v2h16v-2ZM2 18H0a2 2 0 0 0 2 2v-2Zm0 0V4H0v14h2ZM2 4V2a2 2 0 0 0-2 2h2Zm2-3v3h2V1H4Zm5 0v3h2V1H9Zm5 0v3h2V1h-2ZM1 8h18V6H1v2Zm3 3v.01h2V11H4Zm1 1.01h.01v-2H5v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H5v2h.01v-2ZM9 11v.01h2V11H9Zm1 1.01h.01v-2H10v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H10v2h.01v-2ZM9 15v.01h2V15H9Zm1 1.01h.01v-2H10v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H10v2h.01v-2ZM14 15v.01h2V15h-2Zm1 1.01h.01v-2H15v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H15v2h.01v-2ZM14 11v.01h2V11h-2Zm1 1.01h.01v-2H15v2Zm1.01-1V11h-2v.01h2Zm-1-1.01H15v2h.01v-2ZM4 15v.01h2V15H4Zm1 1.01h.01v-2H5v2Zm1.01-1V15h-2v.01h2Zm-1-1.01H5v2h.01v-2Z"/>
                </svg>        <div class="text-sm">
                <slot name="schedule"></slot>
              </div>
              </div>
            </div>-->
    <!--    <div class="w-full flex mb-2 gap-3">-->
    <!--      <div class="w-full flex gap-3 items-center">-->
    <!--        <PhMagnifyingGlass :size="28" />-->
    <!--        <div class="text-sm">-->
    <!--          <slot name="contact"></slot>-->
    <!--        </div>-->
    <!--      </div>-->
    <!--    </div>-->
  </div>
</template>

<style scoped></style>
